import * as React from "react";
import { NavMenuOption, PageLayout } from "../../components/PageLayout/PageLayout";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import { useHistory } from "react-router-dom";
import InsightsIcon from "@mui/icons-material/Insights";

interface MetricDefinition {
  id: string;
  name: string;
  description: string;
}

interface MetricGroup {
  id: string;
  name: string;
  description: string;
  icon: React.ReactElement;
  metrics: MetricDefinition[];
}

const metricGroups: MetricGroup[] = [
  {
    id: "machineTranslationAi",
    name: "Machine Translation / AI",
    description: "Reports related to Machine Translation and AI services.",
    icon: <AutoAwesomeIcon />,
    metrics: [
      {
        id: "mtUsageForClientBilling",
        name: "MT Usage for Client Billing",
        description: "Report used for generating the monthly billing for clients"
      },
      {
        id: "machineTranslationClientUsageAnalytics",
        name: "Machine Translation Usage Analytics",
        description: "Client usage of machine translation"
      },
      {
        id: "machineTranslationRequestCount",
        name: "Machine Translation Requests by Client Contact",
        description: "Statistics for Machine Translation requests by client contact"
      },
      {
        id: "mtEscalationAnalytics",
        name: "Machine Translation Escalations",
        description: "Report on machine translation escalations to human"
      },
      {
        id: "mtEscalationByClientContact",
        name: "Machine Translation Escalations by Client Contact",
        description: "Report on machine translation escalations to human by client contact"
      },
      {
        id: "aiServiceUsageAnalytics",
        name: "AI Service Usage Analytics",
        description: "Report on AI service usage by client"
      },
      {
        id: "fileTypesByClientReports",
        name: "File Types Uploaded by Client",
        description: "Report on file types uploaded by clients for AI Translation"
      },
      {
        id: "lqaRatingAnalytics",
        name: "AQE Rating Analytics - Document Translation",
        description: "Report on linguistic quality and rating of MT-translated documents"
      }
    ]
  },
  {
    id: "otherReports",
    name: "Other Reports",
    description: "Operational, client, and platform-level analytics reports.",
    icon: <FolderOpenOutlinedIcon />,
    metrics: [
      {
        id: "applicationEventAnalytics",
        name: "Application Event Analytics",
        description: "Statistics for different types of application events"
      },
      {
        id: "enabledApplicationsAnalytics",
        name: "Enabled Applications",
        description: "Report on enabled applications"
      },
      {
        id: "quoteRejectionReasonsAnalytics",
        name: "Auto-Quote Rejection Reasons Analytics",
        description: "Report on auto-quote rejection reasons"
      },
      {
        id: "clientContactsReports",
        name: "Client Contacts Report",
        description: "Report on client contacts with detailed information"
      }
    ]
  }
];

const AnalyticsPage: React.FC = () => {
  const history = useHistory();
  const onViewMetricClick = React.useCallback(
    (metricId: string) => {
      history.push(`/analytics/${metricId}`);
    },
    [history]
  );

  return (
    <PageLayout selectedNavMenu={NavMenuOption.ANALYTICS} title="Analytics">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Browse analytics reports by category to find the right report faster.
      </Typography>

      <Box>
        {metricGroups.map(metricGroup => (
          <Accordion
            key={metricGroup.id}
            defaultExpanded={false}
            disableGutters={true}
            elevation={0}
            sx={{
              mb: 2,
              border: theme => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: "hidden",
              "&:before": {
                display: "none"
              }
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1.5 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%", pr: 1 }}>
                <Avatar sx={{ bgcolor: "primary.main" }}>{metricGroup.icon}</Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {metricGroup.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {metricGroup.description}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${metricGroup.metrics.length} report${metricGroup.metrics.length === 1 ? "" : "s"}`}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3 }}>
              <List sx={{ width: "100%", bgcolor: "background.paper" }} disablePadding={true}>
                {metricGroup.metrics.map(metric => (
                  <React.Fragment key={metric.id}>
                    <Divider component="li" />
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => onViewMetricClick(metric.id)} sx={{ minHeight: 80, px: 3 }}>
                        <ListItemIcon>
                          <Avatar>
                            <InsightsIcon />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={metric.name}
                          primaryTypographyProps={{ variant: "button" }}
                          secondary={metric.description}
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </PageLayout>
  );
};

export { AnalyticsPage };
